import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FadeInSection from '../../components/base/FadeInSection';
import DashboardStats from './components/DashboardStats';
import StudySummary from './components/StudySummary';
import DashboardCharts from './components/DashboardCharts';
import StudySchedule from './components/StudySchedule';
import StudyStreak from './components/StudyStreak';
import StudyGoalProgressWidget from './components/StudyGoalProgressWidget';
import RoadmapProgressWidget from './components/RoadmapProgressWidget';
import ResearchActivityWidget from './components/ResearchActivityWidget';
import QuizMasteryOverview from './components/QuizMasteryOverview';
import RankingWidget from './components/RankingWidget';
import StudyStreakRankingWidget from './components/StudyStreakRankingWidget';
import MoodStudyCorrelation from './components/MoodStudyCorrelation';
import DashboardCustomizeModal, {
  DashboardWidgetConfig,
  DEFAULT_WIDGET_CONFIG,
  saveWidgetConfig,
} from './components/DashboardCustomizeModal';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import SortableWidget from '../../components/base/SortableWidget';
import GlobalSearchModal from '../../components/feature/GlobalSearchModal';
import KeyboardShortcutsHelp from '../../components/feature/KeyboardShortcutsHelp';
import ReviewReminder from '../../components/feature/ReviewReminder';
import { supabase } from '../../lib/supabase';

// ユーザーIDごとにlocalStorageキーを分ける
const getStorageKey = (userId: string) => `dashboard_widget_config_${userId}`;

const loadWidgetConfigForUser = (userId: string): DashboardWidgetConfig => {
  try {
    const saved = localStorage.getItem(getStorageKey(userId));
    if (saved) {
      const parsed = JSON.parse(saved) as Partial<DashboardWidgetConfig>;
      return { ...DEFAULT_WIDGET_CONFIG, ...parsed };
    }
  } catch {
    // ignore
  }
  return { ...DEFAULT_WIDGET_CONFIG };
};

const saveWidgetConfigForUser = (userId: string, config: DashboardWidgetConfig): void => {
  try {
    localStorage.setItem(getStorageKey(userId), JSON.stringify(config));
  } catch {
    // ignore
  }
};

const WIDGET_KEY_MAP: Record<string, keyof DashboardWidgetConfig> = {
  schedule: 'showSchedule',
  streak: 'showStreak',
  quizMastery: 'showQuizMastery',
  charts: 'showCharts',
  moodCorrelation: 'showMoodCorrelation',
  pomodoro: 'showPomodoro',
  weeklyReport: 'showWeeklyReport',
  studyGoal: 'showGoalProgress',
  ranking: 'showRanking',
  streakRanking: 'showStudyStreakRanking',
  roadmap: 'showRoadmapProgress',
  researchActivity: 'showResearchActivity',
};

interface DashboardStatsData {
  monthlyPosts: number;
  monthlyHours: number;
  weeklyPosts: number;
  totalPosts: number;
  totalHours: number;
  totalMaterials: number;
  totalVideos: number;
}

export default function DashboardPage() {
  const { user, username, loading } = useAuth();
  const navigate = useNavigate();
  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
  const [widgetConfig, setWidgetConfig] = useState<DashboardWidgetConfig>(DEFAULT_WIDGET_CONFIG);
  const [showDragHint, setShowDragHint] = useState(true);
  
  // 統計データの状態管理
  const [stats, setStats] = useState<DashboardStatsData>({
    monthlyPosts: 0,
    monthlyHours: 0,
    weeklyPosts: 0,
    totalPosts: 0,
    totalHours: 0,
    totalMaterials: 0,
    totalVideos: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [dailyData, setDailyData] = useState<{ date: string; hours: number }[]>([]);
  const [subjectData, setSubjectData] = useState<{ name: string; value: number }[]>([]);

  // センサー設定
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const loadWidgetSettings = useCallback(() => {
    if (!user) return;
    const config = loadWidgetConfigForUser(user.id);
    setWidgetConfig(config);
  }, [user]);

  // 統計データを取得する関数
  const fetchStats = useCallback(async () => {
    if (!user) return;
    
    setStatsLoading(true);
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const startOfWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();

      // 日記の件数と学習時間を取得
      const [
        { count: totalPosts },
        { count: monthlyPosts },
        { count: weeklyPosts },
        { data: allDiaries },
        { count: totalMaterials },
        { count: totalVideos },
      ] = await Promise.all([
        supabase.from('diary_entries').select('*', { count: 'exact', head: true }).eq('user_id', user.id),
        supabase.from('diary_entries').select('*', { count: 'exact', head: true }).eq('user_id', user.id).gte('created_at', startOfMonth),
        supabase.from('diary_entries').select('*', { count: 'exact', head: true }).eq('user_id', user.id).gte('created_at', startOfWeek),
        supabase.from('diary_entries').select('study_hours, subject, created_at').eq('user_id', user.id),
        supabase.from('study_materials').select('*', { count: 'exact', head: true }).eq('user_id', user.id),
        supabase.from('lecture_videos').select('*', { count: 'exact', head: true }).eq('user_id', user.id),
      ]);

      // 学習時間の集計
      const diaries = allDiaries || [];
      const totalHours = diaries.reduce((sum, d) => sum + (Number(d.study_hours) || 0), 0);
      const monthlyHours = diaries
        .filter(d => new Date(d.created_at) >= new Date(startOfMonth))
        .reduce((sum, d) => sum + (Number(d.study_hours) || 0), 0);

      // 日別データの集計（直近30日）
      const dailyMap = new Map<string, number>();
      const last30Days = Array.from({ length: 30 }, (_, i) => {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
        const dateStr = date.toISOString().split('T')[0];
        dailyMap.set(dateStr, 0);
        return dateStr;
      }).reverse();

      diaries.forEach(d => {
        const dateStr = new Date(d.created_at).toISOString().split('T')[0];
        if (dailyMap.has(dateStr)) {
          dailyMap.set(dateStr, dailyMap.get(dateStr)! + (Number(d.study_hours) || 0));
        }
      });

      const dailyDataArray = last30Days.map(date => ({
        date,
        hours: Math.round((dailyMap.get(date) || 0) * 10) / 10,
      }));

      // 科目別データの集計
      const subjectMap = new Map<string, number>();
      diaries.forEach(d => {
        const subject = d.subject || 'その他';
        subjectMap.set(subject, (subjectMap.get(subject) || 0) + (Number(d.study_hours) || 0));
      });

      const subjectDataArray = Array.from(subjectMap.entries())
        .map(([name, value]) => ({ name, value: Math.round(value * 10) / 10 }))
        .sort((a, b) => b.value - a.value);

      setStats({
        monthlyPosts: monthlyPosts || 0,
        monthlyHours: Math.round(monthlyHours * 10) / 10,
        weeklyPosts: weeklyPosts || 0,
        totalPosts: totalPosts || 0,
        totalHours: Math.round(totalHours * 10) / 10,
        totalMaterials: totalMaterials || 0,
        totalVideos: totalVideos || 0,
      });

      setDailyData(dailyDataArray);
      setSubjectData(subjectDataArray);
    } catch (error) {
      console.error('統計データの取得に失敗しました:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadWidgetSettings();
      fetchStats();
      const hintDismissed = localStorage.getItem('dashboard-drag-hint-dismissed');
      setShowDragHint(!hintDismissed);
    }
  }, [user, loadWidgetSettings, fetchStats]);

  // Supabase Realtimeでリアルタイム更新を購読
  useEffect(() => {
    if (!user) return;

    const diaryChannel = supabase
      .channel('dashboard-diary-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'diary_entries', filter: `user_id=eq.${user.id}` }, () => {
        fetchStats();
      })
      .subscribe();

    const materialsChannel = supabase
      .channel('dashboard-materials-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'study_materials', filter: `user_id=eq.${user.id}` }, () => {
        fetchStats();
      })
      .subscribe();

    const videosChannel = supabase
      .channel('dashboard-videos-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'lecture_videos', filter: `user_id=eq.${user.id}` }, () => {
        fetchStats();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(diaryChannel);
      supabase.removeChannel(materialsChannel);
      supabase.removeChannel(videosChannel);
    };
  }, [user, fetchStats]);

  const handleDismissDragHint = () => {
    setShowDragHint(false);
    localStorage.setItem('dashboard-drag-hint-dismissed', 'true');
  };

  const handleSaveWidgetSettings = (newConfig: DashboardWidgetConfig) => {
    setWidgetConfig(newConfig);
    if (user) {
      saveWidgetConfigForUser(user.id, newConfig);
    } else {
      saveWidgetConfig(newConfig);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = widgetConfig.widgetOrder.indexOf(active.id as string);
    const newIndex = widgetConfig.widgetOrder.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = arrayMove(widgetConfig.widgetOrder, oldIndex, newIndex);
    const newConfig = { ...widgetConfig, widgetOrder: newOrder };
    handleSaveWidgetSettings(newConfig);
  };

  const renderWidget = (widgetId: string) => {
    switch (widgetId) {
      case 'schedule': return <StudySchedule />;
      case 'streak': return <StudyStreak />;
      case 'quizMastery': return <QuizMasteryOverview />;
      case 'charts': return <DashboardCharts />;
      case 'moodCorrelation': return <MoodStudyCorrelation />;
      case 'studyGoal': return <StudyGoalProgressWidget />;
      case 'ranking': return <RankingWidget />;
      case 'streakRanking': return <StudyStreakRankingWidget />;
      case 'roadmap': return <RoadmapProgressWidget />;
      case 'researchActivity': return <ResearchActivityWidget />;
      default: return null;
    }
  };

  const visibleWidgets = widgetConfig.widgetOrder.filter((id) => {
    const key = WIDGET_KEY_MAP[id];
    return key ? widgetConfig[key] : false;
  });

  // ローディング中
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 dark:text-gray-400">読み込み中...</p>
        </div>
      </div>
    );
  }

  // 未ログイン時のガード表示
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
        <Header />
        <section className="pt-20 sm:pt-24 pb-16 hero-gradient-animated hero-dark-force relative overflow-hidden flex-1 flex flex-col">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -right-20 w-[420px] h-[420px] bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-32 -left-20 w-[500px] h-[500px] bg-emerald-300/10 rounded-full blur-3xl"></div>
            <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
          </div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 relative flex-1 flex flex-col justify-center py-12">
            <div className="text-center mb-10">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6 border border-white/30">
                <i className="ri-dashboard-line text-4xl text-white"></i>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">ダッシュボード</h1>
              <p className="text-lg text-teal-100">学習の進捗を可視化し、目標達成をサポートします</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 sm:gap-6 mb-10">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-line-chart-line text-2xl text-white"></i>
                </div>
                <h3 className="text-base font-semibold text-white mb-2">学習統計</h3>
                <p className="text-sm text-teal-100">学習時間や進捗をグラフで確認できます</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-calendar-check-line text-2xl text-white"></i>
                </div>
                <h3 className="text-base font-semibold text-white mb-2">学習スケジュール</h3>
                <p className="text-sm text-teal-100">今日の予定と今後の学習計画を管理</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-trophy-line text-2xl text-white"></i>
                </div>
                <h3 className="text-base font-semibold text-white mb-2">目標達成</h3>
                <p className="text-sm text-teal-100">学習目標の進捗と達成状況を追跡</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/login')}
                className="px-8 py-3 bg-white text-teal-700 rounded-xl font-bold hover:bg-teal-50 transition-colors whitespace-nowrap shadow-lg"
              >
                <i className="ri-login-box-line mr-2"></i>ログイン
              </button>
              <button
                onClick={() => navigate('/login')}
                className="px-8 py-3 bg-white/15 text-white border border-white/30 rounded-xl font-bold hover:bg-white/25 transition-colors whitespace-nowrap backdrop-blur-sm"
              >
                <i className="ri-user-add-line mr-2"></i>新規登録
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />

      {/* Hero Header */}
      <div className="hero-dark-force hero-gradient-animated relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 via-purple-600/20 to-pink-600/20 pointer-events-none"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-4xl font-bold text-white">
              {username ? `${username}のダッシュボード` : 'ダッシュボード'}
            </h1>
            <button
              onClick={() => setIsCustomizeOpen(true)}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg font-medium transition-colors flex items-center gap-2 whitespace-nowrap backdrop-blur-sm border border-white/20 cursor-pointer"
            >
              <i className="ri-settings-3-line"></i>
              カスタマイズ
            </button>
          </div>
          <p className="text-xl text-white/80">学習の進捗を一目で確認</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <DashboardStats stats={stats} loading={statsLoading} />
        <StudySummary stats={stats} dailyData={dailyData} subjectData={subjectData} />

        {/* Drag Hint Banner */}
        {showDragHint && visibleWidgets.length > 0 && (
          <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-blue-100 dark:bg-blue-900/40 rounded-lg">
                <i className="ri-drag-move-2-line text-blue-600 dark:text-blue-400 text-xl"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  <span className="hidden sm:inline">ウィジェットをドラッグして並び替えできます</span>
                  <span className="sm:hidden">ウィジェットを長押しして並び替えできます</span>
                </p>
              </div>
            </div>
            <button
              onClick={handleDismissDragHint}
              className="w-8 h-8 flex items-center justify-center hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-lg transition-colors cursor-pointer"
              aria-label="閉じる"
            >
              <i className="ri-close-line text-blue-600 dark:text-blue-400"></i>
            </button>
          </div>
        )}

        {/* Sortable Widgets Grid */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={visibleWidgets} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {visibleWidgets.map((widgetId) => (
                <SortableWidget key={widgetId} id={widgetId}>
                  {renderWidget(widgetId)}
                </SortableWidget>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      <Footer />

      {/* Customize Modal — 条件付きで1つだけ表示 */}
      {isCustomizeOpen && (
        <DashboardCustomizeModal
          config={widgetConfig}
          onClose={() => setIsCustomizeOpen(false)}
          onSave={handleSaveWidgetSettings}
        />
      )}
    </div>
  );
}